/**
 * Buying a plan or a credit pack, on whichever surface the customer happens to be on.
 *
 * Three places in the product sell something (the pricing page, the billing page's top-up grid, the
 * top-up modal) and there will be more. Each one branching on the platform for itself is how the
 * surfaces end up subtly different: one routes to Play, one to StoreKit, and the third quietly
 * keeps calling a hosted checkout that a store build must never open. So the branch lives here,
 * once, and callers simply ask for a tier or a credit amount.
 *
 * This is the last file in the shell that knows there is more than one store. Everything above it
 * asks `storeBillingRequired()` and gets one answer; everything below it is store-specific.
 *
 * The package or tier is identified by its planRegistry meaning, never by a price the caller
 * assembled. Customers pay the store's own localised price, and the entitlement granted is whatever
 * the SERVER's registry says that product is worth. A price shown in the UI never decides what is
 * granted, on any surface.
 */

import { currentBillingPolicy } from "./billingPolicy";
import { fetchPlayConfig, purchaseWithPlay, syncPlayPurchases } from "./playBilling";
import { fetchAppStoreConfig, purchaseWithAppStore, syncAppStorePurchases } from "./appStoreBilling";

export type StorePurchaseOutcome =
  | { status: "granted"; tier?: string; acuAmount?: number }
  /** The customer dismissed the sheet. Not an error, and nothing should be said about it. */
  | { status: "cancelled" }
  /** Awaiting approval (Ask to Buy) or a slow payment method. Nothing has been charged yet. */
  | { status: "pending" }
  | { status: "failed"; reason: string };

/** True when a purchase here must go through the platform's store rather than hosted checkout. */
export function storeBillingRequired(): boolean {
  return currentBillingPolicy().storeBillingRequired;
}

/**
 * Buy a subscription tier.
 *
 * Returns rather than throws, because every caller has to render the outcome and a thrown error
 * would push try/catch back into the components this file exists to keep simple.
 */
export async function purchasePlanFromStore(tier: "starter" | "pro" | "studio"): Promise<StorePurchaseOutcome> {
  try {
    if (currentBillingPolicy().mode === "app_store") {
      const config = await fetchAppStoreConfig();
      const product = config.subscriptions.find(p => p.tier === tier);
      if (!product) return { status: "failed", reason: "That plan is not available on this device." };
      return normalise(await purchaseWithAppStore({ productId: product.productId, config }), { tier });
    }

    const config = await fetchPlayConfig();
    const product = config.subscriptions.find(p => p.tier === tier);
    if (!product) return { status: "failed", reason: "That plan is not available on this device." };
    return normalise(await purchaseWithPlay({ productId: product.productId, type: "subs", config }), { tier });
  } catch (e) {
    return { status: "failed", reason: e instanceof Error ? e.message : "Could not complete the purchase." };
  }
}

/** Buy a credit pack, identified by the credit amount planRegistry defines. */
export async function purchaseCreditsFromStore(acuAmount: number): Promise<StorePurchaseOutcome> {
  try {
    if (currentBillingPolicy().mode === "app_store") {
      const config = await fetchAppStoreConfig();
      const product = config.credits.find(c => c.acuAmount === acuAmount);
      if (!product) return { status: "failed", reason: "That credit pack is not available on this device." };
      return normalise(await purchaseWithAppStore({ productId: product.productId, config }), { acuAmount });
    }

    const config = await fetchPlayConfig();
    const product = config.credits.find(c => c.acuAmount === acuAmount);
    if (!product) return { status: "failed", reason: "That credit pack is not available on this device." };
    return normalise(await purchaseWithPlay({ productId: product.productId, type: "inapp", config }), { acuAmount });
  } catch (e) {
    return { status: "failed", reason: e instanceof Error ? e.message : "Could not complete the purchase." };
  }
}

/**
 * Re-send any purchase that completed on the device but never reached the server.
 *
 * Called once at launch from the native shell. A no-op everywhere the relevant plugin is absent, so
 * calling both is cheaper than deciding which one applies.
 */
export async function syncStorePurchases(): Promise<void> {
  await Promise.all([syncPlayPurchases(), syncAppStorePurchases()]);
}

/** Collapse the two stores' result shapes into one, so callers never learn which store answered. */
function normalise(
  result: { status: string; reason?: string; tier?: string; acuAmount?: number },
  fallback: { tier?: string; acuAmount?: number },
): StorePurchaseOutcome {
  switch (result.status) {
    case "granted":   return { status: "granted", tier: result.tier ?? fallback.tier, acuAmount: result.acuAmount ?? fallback.acuAmount };
    case "cancelled": return { status: "cancelled" };
    case "pending":   return { status: "pending" };
    default:          return { status: "failed", reason: result.reason ?? "That purchase did not complete." };
  }
}
